/**
 * Camera preview dialog: requests a single-frame JPEG from the device
 * via the cameras.snapshot command and renders it. Re-takes on demand.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Camera, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import api from '../services/api';
import { FleetCommand, FleetEvent } from '../types';

interface SnapshotDialogProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  cameraId: string;
  cameraLabel: string;
}

export const SnapshotDialog: React.FC<SnapshotDialogProps> = ({
  open,
  onClose,
  deviceId,
  cameraId,
  cameraLabel,
}) => {
  const [pendingUuid, setPendingUuid] = useState<string | null>(null);
  const [imageData, setImageData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const trigger = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const cmd = await api.enqueueFleetCommand(deviceId, 'cameras.snapshot', {
        camera_id: cameraId,
      });
      setPendingUuid(cmd.uuid);
    } catch (err: any) {
      setError(err?.message || 'snapshot request failed');
      setBusy(false);
    }
  }, [cameraId, deviceId]);

  useEffect(() => {
    if (!open) {
      setImageData(null);
      setError(null);
      setPendingUuid(null);
      setBusy(false);
      return;
    }
    trigger();
  }, [open, trigger]);

  useEffect(() => {
    if (!open || !pendingUuid) return;
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type !== 'command_update') return;
      const cmd: FleetCommand = evt.command;
      if (cmd.uuid !== pendingUuid) return;
      if (cmd.status === 'done') {
        const b64 = (cmd.result?.image_base64 as string | undefined) || '';
        if (b64) {
          setImageData(b64);
        } else {
          setError('snapshot returned no image data');
        }
        setBusy(false);
      } else if (cmd.status === 'failed' || cmd.status === 'timed_out') {
        setError(cmd.error || `command ${cmd.status}`);
        setBusy(false);
      }
    });
    return unsub;
  }, [open, pendingUuid]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-accent">
            <Camera className="size-5" />
            Preview · {cameraLabel}
          </DialogTitle>
        </DialogHeader>

        {busy && !imageData && (
          <div className="flex flex-col items-center gap-3 py-12">
            <Spinner size={28} />
            <span className="text-sm text-fg-secondary">capturing frame from device…</span>
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center gap-3 py-12">
            <span className="font-mono text-status-error">{error}</span>
            <Button variant="ghost" onClick={trigger}>
              <RefreshCw />
              Retry
            </Button>
          </div>
        )}
        {imageData && !error && (
          <div className="space-y-2">
            <img
              src={`data:image/jpeg;base64,${imageData}`}
              alt={cameraLabel}
              className="w-full rounded bg-bg-video"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-fg-muted">
                Captured just now · {cameraId}
              </span>
              <Button variant="ghost" size="sm" onClick={trigger} disabled={busy}>
                <RefreshCw />
                Re-take
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SnapshotDialog;
